import React, { createContext, useState, useContext, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';

const ActivitiesContext = createContext(null);

export const ActivitiesProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [communities, setCommunities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [happeningNow, setHappeningNow] = useState([]);
  const [liveHighlights, setLiveHighlights] = useState([]);
  const [realtime, setRealtime] = useState([]);
  const [trendingHubs, setTrendingHubs] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        // Fetch public data if not already fetched
        if (communities.length === 0) {
          const [roomsData, communitiesData, discussionsData] = await Promise.all([
            api.get('/public/rooms'),
            api.get('/public/communities'),
            api.get('/public/discussions'),
          ]);

          if (!isMounted) return;

          const getValidArray = (res) => {
            if (Array.isArray(res)) return res;
            if (res?.data && Array.isArray(res.data)) return res.data;
            return [];
          };

          const validRooms = getValidArray(roomsData);
          const validDiscussions = getValidArray(discussionsData);
          const validCommunities = getValidArray(communitiesData);

          setRooms(validRooms);
          setCommunities(validCommunities);
          setDiscussions(validDiscussions);

          // Set defaults from public data
          setHappeningNow(validRooms.filter(r => r.status === 'Live').slice(0, 6));
          setLiveHighlights(validDiscussions.slice(0, 3));
          setRealtime(validRooms.filter(r => r.status === 'Live').slice(0, 3));
          setTrendingHubs(validCommunities.slice(0, 3));
        }

        // If user is logged in, fetch personalized feed
        if (userId) {
          try {
            const feed = await api.get('/user/feed');
            if (!isMounted) return;
            
            if (feed) {
              if (Array.isArray(feed.happening_now)) setHappeningNow(feed.happening_now);
              if (Array.isArray(feed.live_highlights)) setLiveHighlights(feed.live_highlights);
              if (Array.isArray(feed.realtime)) setRealtime(feed.realtime);
              if (Array.isArray(feed.trending_hubs)) setTrendingHubs(feed.trending_hubs);
            }
          } catch (err) {
            // Silence unauthenticated errors in feed fetching
            if (err?.status !== 401) {
              console.warn('Failed to fetch personalized feed', err);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch platform data', err);
      }
    };
    
    fetchData();
    return () => { isMounted = false; };
  }, [userId]);

  const addCommunity = async (community) => {
    const data = await api.post('/communities', community);
    setCommunities((prev) => [...prev, data]);
    return data;
  };

  const addRoom = async (room) => {
    const data = await api.post('/rooms', room);
    setRooms((prev) => [...prev, data]);
    return data;
  };

  const addDiscussion = async (discussion) => {
    const data = await api.post('/discussions', discussion);
    setDiscussions((prev) => [...prev, data]);
    return data;
  };

  const addResource = async (resource) => {
    const data = await api.post('/resources', resource);
    return data;
  };

  const updateCommunity = async (id, communityData) => {
    const data = await api.put(`/communities/${id}`, communityData);
    setCommunities((prev) => prev.map(c => String(c.id) === String(id) ? data : c));
    return data;
  };

  return (
    <ActivitiesContext.Provider value={{ 
      communities, 
      rooms, 
      discussions, 
      setRooms,
      setDiscussions,
      happeningNow,
      liveHighlights,
      realtime,
      trendingHubs,
      addCommunity, 
      updateCommunity,
      addRoom, 
      addDiscussion,
      addResource
    }}>
      {children}
    </ActivitiesContext.Provider>
  );
};

export const useActivities = () => useContext(ActivitiesContext);
